import { Button } from '@tempo-io/sui-button';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { toast } from '@tempo-io/sui-sonner';
import { Spinner } from '@tempo-io/sui-spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ItemSyncStatusView,
  SynchronizationStatusValues,
  WorkIntegration_Keys,
} from '@/lib/api/types/work-integrations';
import { useRequestItemSyncMutation } from '@/lib/mutations/work-integrations/useRequestItemSyncMutation';
import { useGetAllWorkIntegrations } from '@/lib/queries/work-integrations/useGetAllWorkIntegrations';
import { useGetWorkIntegrationIdsByItemIds } from '@/lib/queries/work-integrations/useGetWorkIntegrationItems';
import { useItemSyncStatusesQuery } from '@/lib/queries/work-integrations/useItemSyncStatusesQuery';
import { AssignIntegrationDialog } from './AssignIntegrationDialog';

interface IntegrationFieldProps {
  itemId: number;
  loopId: number;
  itemTypeId: number;
}

export const IntegrationField = ({ itemId, loopId, itemTypeId }: IntegrationFieldProps) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const itemIds = useMemo(() => [itemId], [itemId]);

  const { data: integrationIdsByItemId, isLoading: isPushedLoading } =
    useGetWorkIntegrationIdsByItemIds(itemIds);
  const { data: syncStatusesByItemId, isLoading: isSyncStatusLoading } =
    useItemSyncStatusesQuery(itemIds);
  const { data: integrations = [] } = useGetAllWorkIntegrations(
    {},
    { filter: { loopIds: [loopId] } },
  );

  const integrationsById = useMemo(
    () => new Map(integrations.map((integration) => [integration.id, integration])),
    [integrations],
  );

  const eligibleIntegrationsCount = useMemo(
    () =>
      integrations.filter(
        (integration) =>
          integration.sourceSystemType === WorkIntegration_Keys.JIRA &&
          integration.synchronizationStatus === SynchronizationStatusValues.ENABLED,
      ).length,
    [integrations],
  );

  const pushedIntegrationId = integrationIdsByItemId?.[itemId];
  const syncStatus = syncStatusesByItemId?.[itemId];
  const pendingIntegrationId = syncStatus?.integrationId ?? undefined;

  const isPushed = Boolean(pushedIntegrationId);
  const isInProgress =
    !isPushed &&
    pendingIntegrationId !== undefined &&
    syncStatus?.state === ItemSyncStatusView.IN_PROGRESS;
  const isPending =
    !isPushed &&
    pendingIntegrationId !== undefined &&
    syncStatus?.state === ItemSyncStatusView.PENDING;
  const isFailed =
    !isPushed &&
    pendingIntegrationId !== undefined &&
    syncStatus?.state === ItemSyncStatusView.FAILED;
  const isAssigned = isPending || isInProgress || isFailed;

  const associatedIntegrationId: number | undefined = pushedIntegrationId ?? pendingIntegrationId;
  const associatedIntegration =
    associatedIntegrationId !== undefined
      ? integrationsById.get(associatedIntegrationId)
      : undefined;
  const associatedIntegrationName =
    associatedIntegration?.name ??
    (associatedIntegrationId !== undefined
      ? t(($) => $.adaptivePlanner.itemPanel.integration.unnamedFallback, {
          id: associatedIntegrationId,
        })
      : undefined);

  const requestSync = useRequestItemSyncMutation();

  const handleSyncClick = () => {
    requestSync.mutate(
      { itemId },
      {
        onError: () =>
          toast.error(t(($) => $.adaptivePlanner.itemPanel.integration.syncErrorToast)),
      },
    );
  };

  if (isPushedLoading || isSyncStatusLoading) {
    return <Skeleton data-testid="item-panel-integration-field-loading" className="h-6 w-32" />;
  }

  if (isPushed && associatedIntegrationName) {
    return (
      <div
        data-testid="item-panel-integration-field-pushed"
        className="flex items-center gap-2 text-sm text-text-neutral-secondary"
      >
        <span>{associatedIntegrationName}</span>
      </div>
    );
  }

  const assignedTestId = isInProgress
    ? 'item-panel-integration-field-in-progress'
    : isFailed
      ? 'item-panel-integration-field-failed'
      : 'item-panel-integration-field-pending';

  if (isAssigned && associatedIntegrationName && associatedIntegrationId !== undefined) {
    return (
      <>
        <div data-testid={assignedTestId} className="flex items-center gap-2 text-sm">
          {isInProgress ? (
            <span>{associatedIntegrationName}</span>
          ) : (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="text-text-link hover:underline"
            >
              {associatedIntegrationName}
            </button>
          )}
          <Button
            variant="link"
            size="sm"
            disabled={isInProgress || requestSync.isPending}
            className="h-auto p-0 font-normal"
            onClick={handleSyncClick}
          >
            {isInProgress || requestSync.isPending ? (
              <span className="inline-flex items-center gap-1">
                <Spinner size={12} />
                {t(($) => $.adaptivePlanner.itemPanel.integration.syncing)}
              </span>
            ) : (
              t(($) => $.adaptivePlanner.itemPanel.integration.syncButton)
            )}
          </Button>
        </div>
        {!isInProgress && (
          <AssignIntegrationDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            itemId={itemId}
            itemTypeId={itemTypeId}
            integrations={integrations}
            currentIntegrationId={associatedIntegrationId}
          />
        )}
      </>
    );
  }

  const noIntegrationsAvailable = eligibleIntegrationsCount === 0;

  const addButton = (
    <Button
      data-testid="item-panel-integration-add-button"
      variant="outline"
      size="sm"
      onClick={() => setDialogOpen(true)}
      disabled={noIntegrationsAvailable}
    >
      {t(($) => $.adaptivePlanner.itemPanel.integration.addButton)}
    </Button>
  );

  return (
    <>
      {noIntegrationsAvailable ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{addButton}</span>
          </TooltipTrigger>
          <TooltipContent>
            {t(($) => $.adaptivePlanner.itemPanel.integration.addButtonDisabledTooltip)}
          </TooltipContent>
        </Tooltip>
      ) : (
        addButton
      )}
      <AssignIntegrationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        itemId={itemId}
        itemTypeId={itemTypeId}
        integrations={integrations}
      />
    </>
  );
};
